/**
 * Position Thesis Summary Component
 *
 * Compact view of position thesis with key information and Edit button.
 * Shows conviction, duration, stop loss/take profit, and thesis text preview.
 */

import React, { useMemo, useState, useCallback } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Modal, ScrollView } from 'react-native';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { Card } from '../ui/Card';
import { PositionThesisForm } from './PositionThesisForm';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { usePositionTheses } from '../../services/api/queries/usePositionTheses';
import { useQueryClient } from '@tanstack/react-query';
import { parseThesisText } from '../../types/thesis';

interface PositionThesisSummaryProps {
  positionId: number;
  currency: string;
  symbol?: string;
  companyName?: string;
}

export function PositionThesisSummary({
  positionId,
  currency,
  symbol,
  companyName,
}: PositionThesisSummaryProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  const queryClient = useQueryClient();
  const navigation = useNavigation<any>();

  // Navigation callback for chat — runs outside Modal so navigation context is correct
  const handleNavigateToChat = useCallback(
    (params: { initialMessage: string; autoSend?: boolean }) => {
      setShowEditModal(false); // Close the modal first
      // Small delay so modal close animation finishes before navigating
      setTimeout(() => {
        navigation.navigate('Chat', {
          screen: 'ChatScreen',
          params,
        });
      }, 300);
    },
    [navigation]
  );

  const [showEditModal, setShowEditModal] = useState(false);
  const { data: thesisVersions = [], isLoading, isError, refetch } = usePositionTheses(positionId);

  // Get the current (latest) thesis - index 0
  const currentThesis = thesisVersions.length > 0 ? thesisVersions[0] : null;

  // Parse thesis_text to extract statement, pillars, and risks
  const parsedThesis = useMemo(() => {
    if (!currentThesis?.thesis_text) return null;
    return parseThesisText(currentThesis.thesis_text);
  }, [currentThesis?.thesis_text]);

  const formatCurrency = (amount: number | null) => {
    if (amount === null) return 'N/A';
    return `${currency} ${amount.toFixed(2)}`;
  };

  const getDurationLabel = (duration: string | null) => {
    if (!duration) return null;
    switch (duration) {
      case 'Speculative':
        return t('portfolio.durationShortTerm', { defaultValue: 'Speculative' });
      case 'Growth':
        return t('portfolio.durationMidTerm', { defaultValue: 'Growth' });
      case 'Core':
        return t('portfolio.durationCoreHolding', { defaultValue: 'Core' });
      default:
        return duration;
    }
  };

  return (
    <>
      <View style={styles.container}>
        <Card style={styles.card}>
          <View style={styles.header}>
            <Text style={[styles.title, { color: theme.colors.text }]}>
              {t('portfolio.positionDetails.thesis', { defaultValue: 'Thesis' })}
            </Text>
            <TouchableOpacity
              style={[styles.editButton, { backgroundColor: theme.colors.backgroundElevated }]}
              onPress={() => setShowEditModal(true)}
              activeOpacity={0.7}
            >
              <Ionicons name="create-outline" size={16} color={theme.colors.primary} />
              <Text style={[styles.editButtonText, { color: theme.colors.primary }]}>
                {t('portfolio.positionDetails.editThesis', { defaultValue: 'Edit' })}
              </Text>
            </TouchableOpacity>
          </View>

          {isLoading ? (
            <Text style={[styles.loadingText, { color: theme.colors.textSecondary }]}>
              {t('portfolio.loadingThesis', { defaultValue: 'Loading...' })}
            </Text>
          ) : isError ? (
            <View style={styles.placeholder}>
              <Text style={[styles.errorText, { color: theme.colors.error }]}>
                {t('portfolio.thesisLoadError', { defaultValue: 'Failed to load thesis.' })}
              </Text>
              <TouchableOpacity onPress={() => refetch()}>
                <Text style={[styles.retryText, { color: theme.colors.primary }]}>
                  {t('common.retry', { defaultValue: 'Retry' })}
                </Text>
              </TouchableOpacity>
            </View>
          ) : currentThesis ? (
            <View style={styles.content}>
              {/* Strategy Row */}
              {currentThesis.duration && (
                <View style={styles.row}>
                  <View style={styles.metric}>
                    <Text style={[styles.metricLabel, { color: theme.colors.textSecondary }]}>
                      {getDurationLabel(currentThesis.duration)}
                    </Text>
                  </View>
                </View>
              )}

              {/* Stop Loss and Take Profit Row */}
              {(currentThesis.stop_loss_price || currentThesis.take_profit_price) && (
                <View style={styles.row}>
                  {currentThesis.stop_loss_price && (
                    <View style={styles.metric}>
                      <Text style={[styles.metricLabel, { color: theme.colors.textSecondary }]}>
                        {t('portfolio.stopLoss', { defaultValue: 'Stop Loss' })}
                      </Text>
                      <Text style={[styles.metricValue, { color: theme.colors.text }]}>
                        {formatCurrency(currentThesis.stop_loss_price)}
                      </Text>
                    </View>
                  )}
                  {currentThesis.take_profit_price && (
                    <View
                      style={[
                        styles.metric,
                        currentThesis.stop_loss_price && styles.metricWithBorder,
                      ]}
                    >
                      <Text style={[styles.metricLabel, { color: theme.colors.textSecondary }]}>
                        {t('portfolio.takeProfit', { defaultValue: 'Take Profit' })}
                      </Text>
                      <Text style={[styles.metricValue, { color: theme.colors.text }]}>
                        {formatCurrency(currentThesis.take_profit_price)}
                      </Text>
                    </View>
                  )}
                </View>
              )}

              {/* Thesis Text Preview */}
              {parsedThesis && (
                <View style={styles.thesisContainer}>
                  {/* Statement Section */}
                  {parsedThesis.statement && (
                    <View style={styles.section}>
                      <Text style={[styles.sectionLabel, { color: theme.colors.textSecondary }]}>
                        {t('portfolio.positionDetails.thesisStatement', { defaultValue: 'Thesis' })}
                      </Text>
                      <Text style={[styles.sectionContent, { color: theme.colors.text }]}>
                        {parsedThesis.statement}
                      </Text>
                    </View>
                  )}

                  {/* Pillars Section */}
                  {parsedThesis.pillars && parsedThesis.pillars.length > 0 && (
                    <View style={styles.section}>
                      <Text style={[styles.sectionLabel, { color: theme.colors.textSecondary }]}>
                        {t('portfolio.positionDetails.thesisPillars', {
                          defaultValue: 'Key Pillars',
                        })}
                      </Text>
                      {parsedThesis.pillars.map((pillar, index) => (
                        <View key={index} style={styles.bulletItem}>
                          <View
                            style={[styles.bullet, { backgroundColor: theme.colors.secondary }]}
                          />
                          <Text style={[styles.bulletText, { color: theme.colors.text }]}>
                            {pillar}
                          </Text>
                        </View>
                      ))}
                    </View>
                  )}

                  {/* Risks Section */}
                  {parsedThesis.risks && parsedThesis.risks.length > 0 && (
                    <View style={styles.section}>
                      <Text style={[styles.sectionLabel, { color: theme.colors.textSecondary }]}>
                        {t('portfolio.positionDetails.thesisRisks', { defaultValue: 'Key Risks' })}
                      </Text>
                      {parsedThesis.risks.map((risk, index) => (
                        <View key={index} style={styles.bulletItem}>
                          <View style={[styles.bullet, { backgroundColor: theme.colors.error }]} />
                          <Text style={[styles.bulletText, { color: theme.colors.text }]}>
                            {risk}
                          </Text>
                        </View>
                      ))}
                    </View>
                  )}
                </View>
              )}
            </View>
          ) : (
            <View style={styles.placeholder}>
              <Text style={[styles.placeholderText, { color: theme.colors.textSecondary }]}>
                {t('portfolio.noThesis', {
                  defaultValue: 'No investment thesis yet. Add one to track your reasoning.',
                })}
              </Text>
            </View>
          )}
        </Card>
      </View>

      {/* Edit Modal */}
      <Modal
        visible={showEditModal}
        animationType="slide"
        onRequestClose={() => setShowEditModal(false)}
      >
        <View style={[styles.modalContainer, { backgroundColor: theme.colors.background }]}>
          <View style={[styles.modalHeader, { borderBottomColor: theme.colors.border }]}>
            <Text style={[styles.modalTitle, { color: theme.colors.text }]}>
              {t('portfolio.positionDetails.editThesis', { defaultValue: 'Edit Thesis' })}
            </Text>
            <TouchableOpacity
              onPress={() => setShowEditModal(false)}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <Ionicons name="close" size={24} color={theme.colors.text} />
            </TouchableOpacity>
          </View>
          <ScrollView>
            <PositionThesisForm
              positionId={positionId}
              currency={currency}
              symbol={symbol}
              companyName={companyName}
              onSuccess={() => setShowEditModal(false)}
              onNavigateToChat={handleNavigateToChat}
            />
          </ScrollView>
        </View>
      </Modal>
    </>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      marginHorizontal: 12,
      marginTop: 8,
    },
    card: {
      padding: 12,
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 10,
    },
    title: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    editButton: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 10,
      paddingVertical: 4,
      borderRadius: theme.radii.sm,
    },
    editButtonText: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
      marginLeft: 4,
    },
    content: {
      gap: 8,
    },
    row: {
      flexDirection: 'row',
    },
    metric: {
      flex: 1,
    },
    metricWithBorder: {
      borderLeftWidth: 1,
      borderLeftColor: theme.colors.border,
      paddingLeft: 8,
    },
    metricLabel: {
      fontSize: theme.typography.fontSizeXs,
      marginBottom: 2,
    },
    metricValue: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightMedium,
    },
    thesisContainer: {
      marginTop: 4,
    },
    section: {
      marginTop: 8,
    },
    sectionLabel: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
      marginBottom: 4,
    },
    sectionContent: {
      fontSize: theme.typography.fontSizeSm,
      lineHeight: 18,
    },
    bulletItem: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      marginBottom: 4,
    },
    bullet: {
      width: 6,
      height: 6,
      borderRadius: 3,
      marginTop: 6,
      marginRight: 8,
    },
    bulletText: {
      flex: 1,
      fontSize: theme.typography.fontSizeSm,
      lineHeight: 18,
    },
    placeholder: {
      paddingVertical: 8,
    },
    placeholderText: {
      fontSize: theme.typography.fontSizeSm,
      fontStyle: 'italic',
    },
    loadingText: {
      fontSize: theme.typography.fontSizeSm,
      fontStyle: 'italic',
    },
    errorText: {
      fontSize: theme.typography.fontSizeSm,
      marginBottom: 8,
      textAlign: 'center',
    },
    retryText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightMedium,
      marginTop: 4,
    },
    modalContainer: {
      flex: 1,
    },
    modalHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderBottomWidth: 1,
    },
    modalTitle: {
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightBold,
    },
  });
}
